// frontend/src/study_material/StudentStudyMaterial.js
import React, { useState, useEffect } from 'react';
import { API_BASE, authFetch } from '../services/api.js';
import StudyMaterialViewer from './StudyMaterialViewer';
import './StudyMaterial.css';

const StudentStudyMaterial = () => {
  const [student, setStudent] = useState(null);

  // Filter options
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [categories, setCategories] = useState([]);

  // Selected filters
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedChapter, setSelectedChapter] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Results
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [error, setError] = useState('');

  // Viewer
  const [viewerItem, setViewerItem] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsed = JSON.parse(userData);
      setStudent(parsed.student);
      loadSubjects(parsed.student);
    }
  }, []);

  useEffect(() => {
    if (selectedSubject) {
      loadChapters(selectedSubject);
    } else {
      setChapters([]);
    }
    setSelectedChapter('');
    setSelectedTopic('');
    setSelectedCategory('');
    setTopics([]);
    setCategories([]);
    setItems([]);
  }, [selectedSubject]);

  useEffect(() => {
    if (selectedSubject && selectedChapter) {
      loadItems(selectedSubject, selectedChapter, '', '');
      loadFilters(selectedSubject, selectedChapter);
    } else {
      setItems([]);
      setTopics([]);
      setCategories([]);
    }
    setSelectedTopic('');
    setSelectedCategory('');
  }, [selectedChapter]);

  useEffect(() => {
    if (selectedSubject && selectedChapter) {
      setSelectedCategory('');
      loadItems(selectedSubject, selectedChapter, selectedTopic, '');
    }
  }, [selectedTopic]);

  

  const loadSubjects = (studentData) => {
    const accessible = (studentData.accessible_subjects || []).filter(s =>
      s.features && s.features.includes('study_material')
    );
    setSubjects(accessible);
  };

  const loadChapters = async (subjectId) => {
    setError('');
    try {
      const res = await authFetch(
        `${API_BASE}/study-material/student/chapters?subject_id=${subjectId}`
      );
      const data = await res.json();
      if (res.ok) setChapters(data);
      else setError(data.detail || 'Failed to load chapters');
    } catch {
      setError('Failed to load chapters');
    }
  };

  const loadFilters = async (subjectId, chapterId) => {
    setLoadingFilters(true);
    try {
      const res = await authFetch(
        `${API_BASE}/study-material/student/filters?subject_id=${subjectId}&chapter_id=${chapterId}`
      );
      const data = await res.json();
      if (res.ok) {
        setTopics(data.topics);
        setCategories(data.document_categories);
      }
    } catch {
      // non-critical
    } finally {
      setLoadingFilters(false);
    }
  };

  const loadItems = async (subjectId, chapterId, topicId, category) => {
    setLoadingItems(true);
    setError('');
    try {
      let url = `${API_BASE}/study-material/student/items?subject_id=${subjectId}&chapter_id=${chapterId}`;
      if (topicId) url += `&topic_id=${topicId}`;
      const res = await authFetch(url);
      const data = await res.json();
      if (res.ok) {
        let filtered = data;
        if (category) filtered = data.filter(item => item.document_category === category);
        setItems(filtered);
      } else {
        setError(data.detail || 'Failed to load materials');
      }
    } catch {
      setError('Failed to load materials');
    } finally {
      setLoadingItems(false);
    }
  };

  const groupItems = (items) => {
    const categoryMap = {};
    items.forEach(item => {
      const cat = item.document_category || '__others__';
      if (!categoryMap[cat]) categoryMap[cat] = {};
      const topic = item.topic_id || '__notopic__';
      if (!categoryMap[cat][topic]) categoryMap[cat][topic] = { topic_name: item.topic_name || null, items: [] };
      categoryMap[cat][topic].items.push(item);
    });
    return categoryMap;
  };

  const getItemIcon = (type) => ({ pdf: '📄', image: '🖼️', doc: '📝', youtube: '▶️', html: '🌐', link: '🔗' }[type] || '📎');
  const getItemTypeColor = (type) => ({ pdf: '#dc2626', image: '#7c3aed', doc: '#2563eb', youtube: '#ff0000', html: '#059669', link: '#0284c7' }[type] || '#6b7280');

  const renderItem = (item, serial) => (
    <div key={item.item_id} className="sm-item-card" onClick={() => setViewerItem(item)}>
      <span className="sm-item-serial">{serial}.</span>
      <div className="sm-item-icon" style={{ color: getItemTypeColor(item.item_type) }}>
        {getItemIcon(item.item_type)}
      </div>
      <span className="sm-item-title">{item.title}</span>
    </div>
  );

  const renderResults = () => {
    if (!selectedSubject || !selectedChapter) return null;
    if (loadingItems) return <div className="sm-loading">Loading materials...</div>;
    if (error) return <div className="sm-error">⚠️ {error}</div>;
    if (items.length === 0) return (
      <div className="sm-empty">
        <span>📭</span>
        <p>No study materials found for the selected filters.</p>
      </div>
    );

    const filteredItems = selectedCategory
      ? items.filter(item => item.document_category === selectedCategory)
      : items;

    const categoryMap = groupItems(filteredItems);

    return (
      <div className="sm-items-container">
        {Object.entries(categoryMap).map(([cat, topicMap]) => {
          let serial = 0;
          return (
            <div key={cat} className="sm-category-group">
              <div className="sm-category-heading">
                {cat === '__others__' ? 'Others' : cat}
              </div>
              {Object.entries(topicMap).map(([topicKey, topicData]) => (
                <div key={topicKey}>
                  {topicData.topic_name && (
                    <div className="sm-topic-heading">
                      <span className="sm-topic-icon">📌</span>
                      <span>{topicData.topic_name}</span>
                    </div>
                  )}
                  <div className="sm-items-list">
                    {topicData.items.map(item => renderItem(item, ++serial))}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="sm-container">
      <div className="sm-page">

        <div className="sm-page-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <button 
                onClick={() => window.history.back()} 
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#3b82f6', padding: '0' }}
                >
                ←
                </button>
                <h2 style={{ margin: 0 }}>📚 Study Material</h2>
            </div>
          
        </div>

        <div className="sm-filter-panel">
          <div className="sm-filter-row">
            <div className="sm-filter-group">
              <label className="sm-filter-label">Subject</label>
              <select className="sm-filter-select" value={selectedSubject} onChange={e => setSelectedSubject(e.target.value)}>
                <option value="">Select Subject</option>
                {subjects.map(s => <option key={s.subject_id} value={s.subject_id}>{s.subject_name}</option>)}
              </select>
            </div>
            <div className="sm-filter-group">
              <label className="sm-filter-label">Chapter</label>
              <select className="sm-filter-select" value={selectedChapter} onChange={e => setSelectedChapter(e.target.value)} disabled={!selectedSubject || chapters.length === 0}>
                <option value="">Select Chapter</option>
                {chapters.map(c => <option key={c.chapter_id} value={c.chapter_id}>{c.name}</option>)}
              </select>
            </div>
          </div>

          {selectedChapter && (
            <div className="sm-filter-row">
              <div className="sm-filter-group">
                <label className="sm-filter-label">Topic <span className="sm-optional">(optional)</span></label>
                <select className="sm-filter-select" value={selectedTopic} onChange={e => setSelectedTopic(e.target.value)} disabled={loadingFilters || topics.length === 0}>
                  <option value="">All Topics</option>
                  {topics.map(t => <option key={t.topic_id} value={t.topic_id}>{t.name}</option>)}
                </select>
              </div>
              <div className="sm-filter-group">
                <label className="sm-filter-label">Category <span className="sm-optional">(optional)</span></label>
                <select className="sm-filter-select" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} disabled={loadingFilters || categories.length === 0}>
                  <option value="">All Categories</option>
                  {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>

        {selectedChapter && <div className="sm-divider" />}

        {renderResults()}

      </div>

      {viewerItem && (
    <StudyMaterialViewer
      item={viewerItem}
      onClose={() => setViewerItem(null)}
    />
  )}
    </div>
  );
};

export default StudentStudyMaterial;